import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Sélecteur de date maison (remplace `<input type="date">`).
 * Calendrier mensuel mettant en évidence les week-ends ; sélection d'un jour précis.
 *
 * Props :
 *  - value        : date ISO 'yyyy-mm-dd' (ou '' si aucune)
 *  - onChange     : (isoDate: string) => void   — reçoit la date choisie ('' si effacée)
 *  - disabled?    : bool
 *  - min?, max?   : bornes ISO 'yyyy-mm-dd' (jours hors plage non sélectionnables)
 *  - required?    : bool (n'autorise pas l'effacement depuis le popover)
 *  - id?, ariaLabel?, autoFocus?
 *  - style?       : styles fusionnés sur le déclencheur (pour matcher les inputs du projet)
 */

const JOURS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

function iso(d) {
  return d.toISOString().slice(0, 10);
}
function moisCourantISO() {
  return new Date().toISOString().slice(0, 7); // 'yyyy-mm' sans heure locale ambiguë
}

export default function DatePicker({
  value = '',
  onChange,
  disabled = false,
  min,
  max,
  required = false,
  id,
  ariaLabel,
  autoFocus = false,
  style,
}) {
  const [ouvert, setOuvert] = useState(false);
  const [curseur, setCurseur] = useState(() =>
    new Date(`${(value ? value.slice(0, 7) : moisCourantISO())}-01T00:00:00Z`),
  );
  const declRef = useRef(null);
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  // Positionnement du popover (portail en position fixed) borné au viewport.
  // Ticket #79 : le calendrier s'ouvrait toujours SOUS le déclencheur ; un
  // champ situé bas dans une modale (ex. « fin de récurrence ») sur un écran
  // de laptop le faisait sortir de l'écran. On mesure la hauteur réelle du
  // popover (rendu d'abord invisible, avant peinture) puis on l'ouvre en
  // dessous s'il y a la place, sinon au-dessus, sinon on le cale dans le
  // viewport. Si le viewport est plus petit que le calendrier, il scrolle.
  useLayoutEffect(() => {
    if (!ouvert) {
      setPos(null);
      return undefined;
    }
    const placer = () => {
      if (!declRef.current || !popRef.current) return;
      const marge = 8;
      const ecart = 4;
      const r = declRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const largeur = popRef.current.offsetWidth || 196;
      const hauteurMax = Math.max(0, vh - 2 * marge);
      const hauteur = Math.min(popRef.current.scrollHeight, hauteurMax);
      let top;
      if (r.bottom + ecart + hauteur <= vh - marge) {
        top = r.bottom + ecart; // en dessous (cas nominal)
      } else if (r.top - ecart - hauteur >= marge) {
        top = r.top - ecart - hauteur; // au-dessus
      } else {
        top = Math.max(marge, vh - marge - hauteur); // calé en bas du viewport
      }
      const left = Math.max(marge, Math.min(r.left, vw - largeur - marge));
      setPos({ top, left, maxHeight: hauteurMax });
    };
    placer();
    window.addEventListener('resize', placer);
    return () => window.removeEventListener('resize', placer);
    // `value`/`required` changent la présence de la ligne « Effacer » (hauteur).
  }, [ouvert, value, required]);

  useEffect(() => {
    if (!ouvert) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOuvert(false);
    const onClick = (e) => {
      const t = e.target;
      if (!popRef.current?.contains(t) && !declRef.current?.contains(t)) setOuvert(false);
    };
    // Scroll de la page/modale → on ferme (le popover ne suit pas son ancre).
    // Sauf scroll interne au popover lui-même (viewport très bas, ticket #79).
    const onScroll = (e) => {
      if (popRef.current && e.target instanceof Node && popRef.current.contains(e.target)) return;
      setOuvert(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [ouvert]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoFocus && !disabled) setOuvert(true);
  }, []);

  function ouvrir() {
    if (disabled) return;
    if (value) setCurseur(new Date(`${value.slice(0, 7)}-01T00:00:00Z`));
    setOuvert(true);
  }

  const annee = curseur.getUTCFullYear();
  const mois = curseur.getUTCMonth();
  const premier = new Date(Date.UTC(annee, mois, 1));
  const decalLundi = (premier.getUTCDay() + 6) % 7; // 0 = lundi
  const debutGrille = new Date(premier);
  debutGrille.setUTCDate(1 - decalLundi);
  const cases = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(debutGrille);
    d.setUTCDate(debutGrille.getUTCDate() + i);
    return d;
  });

  const horsBornes = (isoD) => (min && isoD < min) || (max && isoD > max);

  const libelle = value
    ? new Date(`${value}T00:00:00Z`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'UTC' })
    : 'jj/mm/aaaa';

  const styleDecl = {
    display: 'inline-flex', alignItems: 'center', gap: 6, cursor: disabled ? 'not-allowed' : 'pointer',
    textAlign: 'left', opacity: disabled ? 0.55 : 1,
    width: '100%', padding: '8px 10px', background: 'var(--glass)', color: value ? 'var(--text)' : 'var(--text-muted)',
    border: '1px solid var(--glass-border)', borderRadius: 'var(--radius-sm)', fontSize: 13,
    ...style,
  };

  const pop = ouvert
    ? ReactDOM.createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed', top: pos?.top ?? 0, left: pos?.left ?? 0, zIndex: 4000, width: 196,
            // 1er rendu (avant mesure) invisible : le layout effect le place avant peinture.
            visibility: pos ? 'visible' : 'hidden',
            maxHeight: pos?.maxHeight, overflowY: 'auto',
            background: 'var(--card-bg)', border: '1px solid var(--glass-border)', borderRadius: 10,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)', padding: 6,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <button type="button" onClick={() => setCurseur(new Date(Date.UTC(annee, mois - 1, 1)))} title="Mois précédent" style={navBtn}>
              <ChevronLeft size={16} />
            </button>
            <span style={{ fontWeight: 600, fontSize: 12.5, textTransform: 'capitalize' }}>
              {MOIS[mois]} {annee}
            </span>
            <button type="button" onClick={() => setCurseur(new Date(Date.UTC(annee, mois + 1, 1)))} title="Mois suivant" style={navBtn}>
              <ChevronRight size={16} />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {JOURS.map((j, i) => (
              <span key={i} style={{ textAlign: 'center', fontSize: 9.5, fontWeight: 700, color: 'var(--text-muted)', padding: '2px 0' }}>
                {j}
              </span>
            ))}
            {cases.map((d) => {
              const isoD = iso(d);
              const horsMois = d.getUTCMonth() !== mois;
              const we = d.getUTCDay() === 0 || d.getUTCDay() === 6;
              const selected = !!value && isoD === value;
              const bloque = horsBornes(isoD);
              return (
                <button
                  type="button"
                  key={isoD}
                  disabled={bloque}
                  onClick={() => {
                    onChange?.(isoD);
                    setOuvert(false);
                  }}
                  style={{
                    position: 'relative', height: 23, minWidth: 0, padding: 0, gap: 0, border: 'none',
                    borderRadius: 5, cursor: bloque ? 'not-allowed' : 'pointer',
                    fontSize: 11.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: selected ? '#fff' : 'var(--text)',
                    fontWeight: we ? 700 : 400,
                    opacity: bloque ? 0.3 : horsMois ? 0.45 : 1,
                    background: selected
                      ? 'var(--accent)'
                      : we
                        ? 'color-mix(in srgb, var(--text-muted) 16%, transparent)'
                        : 'transparent',
                  }}
                >
                  {d.getUTCDate()}
                </button>
              );
            })}
          </div>

          {value && !required && (
            <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid var(--glass-border)', textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => { onChange?.(''); setOuvert(false); }}
                style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 11.5, padding: 0 }}
              >
                Effacer
              </button>
            </div>
          )}
        </div>,
        document.body,
      )
    : null;

  return (
    <>
      <button
        type="button"
        ref={declRef}
        id={id}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => (ouvert ? setOuvert(false) : ouvrir())}
        style={styleDecl}
      >
        <CalendarDays size={14} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <span>{libelle}</span>
      </button>
      {pop}
    </>
  );
}

const navBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 2,
  background: 'transparent', border: '1px solid var(--glass-border)', borderRadius: 6,
  color: 'var(--text)', cursor: 'pointer',
};
